import React from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';

import UserGuideContent from './UserGuideContent';
import { UserGuideModalStyles } from './style';
import { UserGuideConfig, UserGuideTabId } from '../types';
import {
  GUIDE_ANNOUNCEMENTS_TAB_ID,
  getGuideTab,
  getGuideTopic,
  hasRecentAnnouncements,
  isTopicRecentlyUpdated,
  normalizeGuideConfig,
} from '../utils/userGuide';

type UserGuideModalProps = {
  config?: UserGuideConfig;
  errorMessage?: string;
  isLoading?: boolean;
  onHide: () => void;
  show: boolean;
};

export default function UserGuideModal({
  config,
  errorMessage,
  isLoading = false,
  onHide,
  show,
}: UserGuideModalProps) {
  const [activeTabId, setActiveTabId] =
    React.useState<UserGuideTabId>('self-serve-tv-lab');
  const [activeTopicId, setActiveTopicId] = React.useState<string | null>(null);
  const guideConfig = React.useMemo(
    () => normalizeGuideConfig(config),
    [config],
  );

  const activeTab = getGuideTab(guideConfig, activeTabId);
  const activeTopic = getGuideTopic(activeTab, activeTopicId);

  React.useEffect(() => {
    if (!guideConfig?.tabs.length) return;

    const nextActiveTab =
      getGuideTab(guideConfig, activeTabId) || guideConfig.tabs[0] || undefined;
    if (!nextActiveTab) return;

    if (nextActiveTab.id !== activeTabId) {
      setActiveTabId(nextActiveTab.id);
      return;
    }

    if (!nextActiveTab.topics.some(topic => topic.id === activeTopicId)) {
      setActiveTopicId(nextActiveTab.topics[0]?.id || null);
    }
  }, [activeTabId, activeTopicId, guideConfig]);

  const showAnnouncementHighlight = hasRecentAnnouncements(guideConfig);

  return (
    <UserGuideModalStyles
      centered
      dialogClassName="user-guide-dialog"
      onHide={onHide}
      show={show}
    >
      <Modal.Body>
        {isLoading ? (
          <div className="guide-empty-state">
            <div className="guide-spinner-card">
              <div className="guide-spinner-ring" />
              <p>Loading guide content…</p>
            </div>
          </div>
        ) : errorMessage ? (
          <div className="guide-error-state">
            <Alert className="mb-0" variant="danger">
              {errorMessage}
            </Alert>
          </div>
        ) : (
          <div className="guide-layout">
            {/* ── Sidebar ── */}
            <aside className="guide-sidebar">
              <div className="guide-sidebar-header">
                <div className="guide-brand">
                  <span className="guide-brand-dot" />
                  <span className="guide-brand-label">TV Lab Portal</span>
                </div>
                <h2 className="guide-sidebar-title">User Guide</h2>

                {/* Tab pills */}
                <div className="guide-tab-pills" role="tablist">
                  {(guideConfig?.tabs || []).map(tab => {
                    const isActive = tab.id === activeTabId;
                    const shouldHighlight =
                      tab.id === GUIDE_ANNOUNCEMENTS_TAB_ID &&
                      showAnnouncementHighlight;
                    return (
                      <button
                        className={`guide-tab-pill${isActive ? ' active' : ''}`}
                        key={tab.id}
                        onClick={() => setActiveTabId(tab.id)}
                        type="button"
                      >
                        {tab.label}
                        {shouldHighlight && (
                          <span className="guide-new-dot" aria-label="New" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Topics section */}
              <div className="guide-topics-section">
                <div className="guide-topics-section-header">
                  <span className="guide-topics-section-label">
                    {activeTab?.layout?.leftPanelTitle || 'Topics'}
                  </span>
                  {activeTab?.topics.length ? (
                    <span className="guide-topics-count">{activeTab.topics.length}</span>
                  ) : null}
                </div>
                <div className="guide-topic-list">
                {activeTab?.topics.length ? (
                  activeTab.topics.map((topic, idx) => {
                    const isActive = topic.id === activeTopicId;
                    const showRecentBadge =
                      activeTab.id === GUIDE_ANNOUNCEMENTS_TAB_ID &&
                      isTopicRecentlyUpdated(
                        topic,
                        guideConfig?.announcementHighlightDays || 0,
                      );
                    return (
                      <button
                        className={`guide-topic-row${isActive ? ' active' : ''}`}
                        key={topic.id}
                        onClick={() => setActiveTopicId(topic.id)}
                        type="button"
                      >
                        <span className="guide-topic-num">{idx + 1}</span>
                        <span className="guide-topic-label">{topic.title}</span>
                        {showRecentBadge && (
                          <span className="guide-recent-badge">New</span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <p className="guide-topic-empty">No topics yet.</p>
                )}
                </div>
              </div>
            </aside>

            {/* ── Content pane ── */}
            <div className="guide-content-pane">
              {/* Header bar */}
              <div className="guide-content-header-bar">
                <div className="guide-content-header-meta">
                  <span className="guide-content-eyebrow">
                    {activeTab?.layout?.eyebrow || 'Guide'}
                  </span>
                  <h3 className="guide-content-heading">
                    {activeTopic?.title || activeTab?.layout?.emptyStateTitle || 'Select a topic'}
                  </h3>
                </div>
                <button
                  aria-label="Close"
                  className="guide-close-btn"
                  onClick={onHide}
                  type="button"
                >
                  ×
                </button>
              </div>

              {/* Scrollable content */}
              <div className="guide-content-scroll">
                <UserGuideContent
                  highlightBadge={
                    activeTab?.id === GUIDE_ANNOUNCEMENTS_TAB_ID &&
                    activeTopic &&
                    isTopicRecentlyUpdated(
                      activeTopic,
                      guideConfig?.announcementHighlightDays || 0,
                    ) ? (
                      <span className="guide-recent-badge">New</span>
                    ) : undefined
                  }
                  layout={activeTab?.layout || { eyebrow: '', title: '', description: '', leftPanelTitle: '', emptyStateTitle: '', emptyStateDescription: '' }}
                  topic={activeTopic}
                />
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </UserGuideModalStyles>
  );
}
